import { CreateViolationBody, Status, Violation } from "types";
import {
  createTenantViolation,
  excludeStandardViolation,
  includeStandardViolation,
  updateTenantViolation,
  deleteTenantViolation,
} from "api";

import { ActionFunction } from "react-router";
import { ViolationsActionHandlers } from "./Violations";

export interface ViolationsActionResponse {
  violation?: Violation;
  message: string;
  success: boolean;
  handler: ViolationsActionHandlers;
}

const violationsAction: ActionFunction = async ({
  request,
}): Promise<ViolationsActionResponse> => {
  const { handler, data, tenantID } = await request.json();
  let actionResponse: ViolationsActionResponse;

  switch (handler as ViolationsActionHandlers) {
    case ViolationsActionHandlers.CREATE:
      actionResponse = await createViolation(data, handler);
      break;
    case ViolationsActionHandlers.INCLUDE:
      if (data?.id >= 100) {
        actionResponse = await updateViolation(data, handler, tenantID);
      } else {
        actionResponse = await includeViolation(data, handler, tenantID);
      }
      break;
    case ViolationsActionHandlers.EXCLUDE:
      if (data?.id >= 100) {
        // tenant ID's start at 100
        actionResponse = await disableViolation(data, handler, tenantID);
      } else {
        actionResponse = await excludeViolation(data, handler, tenantID);
      }
      break;
    case ViolationsActionHandlers.DELETE:
      actionResponse = await deleteViolation(data, handler, tenantID);
      break;
    default:
      actionResponse = {
        success: false,
        message: "unknown handler submitted to action",
        handler: handler,
      };
  }

  return actionResponse;
};

async function createViolation(
  data: CreateViolationBody,
  handler: ViolationsActionHandlers,
): Promise<ViolationsActionResponse> {
  const newViolation = {
    name: data.name,
    description: data.description,
    status: data.status,
    tenantID: data.tenantID,
  };

  try {
    const res = await createTenantViolation(newViolation);
    if (res.ok) {
      return {
        success: true,
        message: `Violation '${newViolation.name}' created!`,
        handler: handler,
        violation: await res.json(),
      };
    } else {
      throw new Error(`${res.status} ${res.statusText}`);
    }
  } catch (e) {
    const error = e as Error;
    return {
      success: false,
      message: error.message,
      handler: handler,
      violation: { id: 0, ...newViolation },
    };
  }
}

async function updateViolation(
  data: Violation,
  handler: ViolationsActionHandlers,
  tenantID: number,
): Promise<ViolationsActionResponse> {
  const include = handler === ViolationsActionHandlers.INCLUDE;
  try {
    if (include) {
      data.status = Status.Active;
    } else {
      data.status = Status.Disabled;
    }
    const res = await updateTenantViolation(tenantID, data);
    if (res.ok) {
      return {
        success: true,
        message: `Violation '${data.name}' ${include ? "active" : "inactive"}!`,
        handler: handler,
      };
    } else {
      // set the status back to what it was
      data.status = include ? Status.Disabled : Status.Active;
      throw new Error(`${res.status} ${res.statusText}`);
    }
  } catch (e) {
    const error = e as Error;
    return {
      success: false,
      message: error.message,
      handler: handler,
      violation: data,
    };
  }
}

async function excludeViolation(
  data: Violation,
  handler: ViolationsActionHandlers,
  tenantID: number,
): Promise<ViolationsActionResponse> {
  try {
    const res = await excludeStandardViolation(tenantID, data.id);
    if (res.ok) {
      return {
        success: true,
        message: `'${data.name}' removed from active violations list`,
        handler: handler,
      };
    } else {
      throw new Error(`${res.status} ${res.statusText}`);
    }
  } catch (e) {
    const error = e as Error;
    return {
      success: false,
      message: error.message,
      handler: handler,
      violation: data,
    };
  }
}

async function includeViolation(
  data: Violation,
  handler: ViolationsActionHandlers,
  tenantID: number,
): Promise<ViolationsActionResponse> {
  try {
    const res = await includeStandardViolation(tenantID, data.id);
    if (res.ok) {
      return {
        success: true,
        message: `Violation '${data.name}' added to active violations list`,
        handler: handler,
      };
    } else {
      throw new Error(`${res.status} ${res.statusText}`);
    }
  } catch (e) {
    const error = e as Error;
    return {
      success: false,
      message: error.message,
      handler: handler,
      violation: data,
    };
  }
}

async function disableViolation(
  data: Violation,
  handler: ViolationsActionHandlers,
  tenantID: number,
): Promise<ViolationsActionResponse> {
  try {
    data.status = Status.Disabled;
    const res = await updateTenantViolation(tenantID, data);
    if (res.ok) {
      return {
        success: true,
        message: `'${data.name}' Disabled`,
        handler: handler,
      };
    } else {
      throw new Error(`${res.status} ${res.statusText}`);
    }
  } catch (e) {
    const error = e as Error;
    return {
      success: false,
      message: error.message,
      handler: handler,
      violation: data,
    };
  }
}

async function deleteViolation(
  data: Violation,
  handler: ViolationsActionHandlers,
  tenantID: number,
): Promise<ViolationsActionResponse> {
  try {
    const res = await deleteTenantViolation(tenantID, data.id);
    if (res.ok) {
      return {
        success: true,
        message: `'${data.name}' Deleted`,
        handler: handler,
      };
    } else {
      throw new Error(`${res.status} ${res.statusText}`);
    }
  } catch (e) {
    const error = e as Error;
    return {
      success: false,
      message: error.message,
      handler: handler,
      violation: data,
    };
  }
}

export default violationsAction;
